/**
 * The request-access form's newsletter opt-in and privacy-notice record, read off a
 * signup's `waitlist_submission` blob for the three grid columns in `consentColumns.tsx`.
 *
 * ## What vera-website sends (since 2026-10-08)
 *
 * `forwardWaitlist` in vera-website's `lib/crm.ts` carries five keys this file reads. The
 * waitlist endpoint DECLARES and validates each on `WaitlistSignupBodyDto` (API
 * `dto/signup/waitlist-signup.dto.ts`) and stores them in the blob as sent
 * (`CreateWaitlistSignupUseCase` spreads the non-column keys in). Validated since
 * 2026-10-08 — before that the body was never validated at all, so an older row's values
 * are whatever the caller sent; the readers below already treat malformed as absent:
 *
 *   newsletterOptIn       boolean. `true` only when the box was ticked at submit.
 *   newsletterDefault     'ticked' (the US default) | 'unticked' | 'unknown' (no box on
 *                         the submitting page). A pre-ticked box left ticked is a weaker
 *                         act than a tick, so the answer is never shown without this.
 *   country               ISO 3166 alpha-2 from the site's /api/geo, or ''. IP-derived:
 *                         the input that chose the box's default, not a verified fact
 *                         about the brand.
 *   privacyNoticeVersion  the privacy policy version the notice linked to, e.g.
 *                         '6 October 2026'.
 *   privacyNoticeShownAt  ISO instant the site's server recorded the notice as shown.
 *
 * ## Older rows
 *
 * Every row written before that change has none of these keys, and nothing here guesses:
 * each reader returns `null`, which the column renders as the house em-dash. In particular
 * a missing `newsletterOptIn` is NOT "No" — the question had not been forwarded yet.
 *
 * Pure (no React, no MUI, no runtime imports) so the API's jest guards can import it the
 * same way they import `websiteFields.ts`.
 */
/**
 * A `SignupEntry['waitlist_submission']`, typed structurally rather than imported so this
 * file has no imports at all (the API's jest config does not resolve the platform's
 * `hooks/...` path alias).
 */
type Blob = { [key: string]: unknown }

/** The wire keys, declared once. Mirrors the website's `WaitlistSubmission` type. */
export const WEBSITE_CONSENT_FIELDS = [
  'newsletterOptIn',
  'newsletterDefault',
  'country',
  'privacyNoticeVersion',
  'privacyNoticeShownAt',
] as const

const COUNTRY = /^[A-Z]{2}$/

/** The ISO alpha-2 code, or `null` when absent, blank, or malformed. */
export const consentCountryCode = (ws: Blob | null | undefined): string | null => {
  const v = ws?.country
  return typeof v === 'string' && COUNTRY.test(v) ? v : null
}

/**
 * "United States (US)" where the runtime knows the region name, else just "US".
 * `Intl.DisplayNames` is in every browser the platform supports; the fallback is for a
 * runtime without full ICU, where a bare code is still the true answer.
 */
export const consentCountryLabel = (ws: Blob | null | undefined): string | null => {
  const code = consentCountryCode(ws)
  if (!code) return null
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code)
    return name && name !== code ? `${name} (${code})` : code
  } catch {
    return code
  }
}

const DEFAULT_WORDS: Record<string, string> = {
  ticked: 'pre-ticked',
  unticked: 'unticked',
  unknown: 'no opt-in box',
}

/**
 * "Yes (pre-ticked, US)", "No (unticked, FR)", "No (no opt-in box)" — or `null` for a row
 * that predates the field.
 *
 * Only a real boolean counts. The site sends exactly that; a looser reading ("on",
 * "true") would be a looser record of consent, which is the wrong direction to be lenient.
 */
export const newsletterLabel = (ws: Blob | null | undefined): string | null => {
  const opted = ws?.newsletterOptIn
  if (typeof opted !== 'boolean') return null

  const d = ws?.newsletterDefault
  const shown = typeof d === 'string' && DEFAULT_WORDS[d] ? DEFAULT_WORDS[d] : null
  const code = consentCountryCode(ws)
  // The country only explains a box that was shown; for "no opt-in box" it explains nothing.
  const detail = [shown, shown && d !== 'unknown' ? code : null].filter(Boolean).join(', ')

  return `${opted ? 'Yes' : 'No'}${detail ? ` (${detail})` : ''}`
}

/**
 * The privacy-notice record, or `null` when the row carries neither half.
 * Either half may be `null` on its own — a malformed timestamp is dropped, not shown raw.
 */
export const privacyNoticeOf = (
  ws: Blob | null | undefined,
): { version: string | null; shownAt: string | null } | null => {
  const v = ws?.privacyNoticeVersion
  const at = ws?.privacyNoticeShownAt
  const version = typeof v === 'string' && v.trim() ? v.trim() : null
  const shownAt = typeof at === 'string' && !Number.isNaN(Date.parse(at)) ? at : null
  return version || shownAt ? { version, shownAt } : null
}
